/**
 * Pipeline tab column model — pure module (unit-tested).
 *
 * Column prefs (visibility + order) persist per browser in localStorage and
 * are reconciled against the live sheet's header row on every load: stored
 * headers that vanished from the sheet are dropped, new sheet headers are
 * appended hidden. Prefs store RAW header strings exactly as they appear on
 * the sheet (e.g. "Closing Date " keeps its trailing space) so row-index
 * lookups stay aligned; metadata lookups trim.
 */

export const STORAGE_KEY = 'payroll.pipeline.columns.v1';

/** Default visible columns, in order (product decision — see plan). */
export const DEFAULT_ORDER = [
  'Loan Number',
  'Client',
  'Primary LO',
  'Primary Commission',
  'Secondary LO',
  'Secondary Commission',
  'Loan Amount',
  'Check',
  'Revenue',
  'Lender',
  'Investor',
  'Comp Code',
  'State',
  'Product',
  'Comp Formula Used',
  'Correspondent',
  'Payroll Batch',
  'Payroll Flags',
];

/** Columns written by payroll runs — read-only in the Pipeline tab (mirrors functions/Payroll/sheets/pipelineGrid.js). */
export const ENGINE_OWNED = new Set([
  'Primary Commission',
  'Secondary Commission',
  'Comp Formula Used',
  'Payroll Flags',
  'Payroll Batch',
]);

// type: money | date | number | text — drives formatting, alignment, and
// what an edit sends (money/number → Number, date → USER_ENTERED string).
const META = {
  'Loan Amount': { type: 'money', width: 110 },
  Check: { type: 'money', width: 110 },
  Revenue: { type: 'money', width: 110 },
  'Loan Number': { type: 'text', width: 110 },
  Lender: { type: 'text', width: 170 },
  Client: { type: 'text', width: 200 },
  'Primary Commission': { type: 'money', width: 130 },
  'Primary LO': { type: 'text', width: 190 },
  'Secondary Commission': { type: 'money', width: 130 },
  'Secondary LO': { type: 'text', width: 190 },
  'Comp Code': { type: 'text', width: 220 },
  'Transfer Date': { type: 'date', width: 100 },
  Reimbur: { type: 'money', width: 100 },
  'Reimbursement - Credit Costs': { type: 'money', width: 130 },
  'CD - Credit Costs': { type: 'money', width: 130 },
  Tolerance: { type: 'money', width: 100 },
  'App Date': { type: 'date', width: 100 },
  'Sub Date': { type: 'date', width: 100 },
  'Pipeline Date': { type: 'date', width: 100 },
  Month: { type: 'text', width: 100 },
  'Closing Date': { type: 'date', width: 110 },
  State: { type: 'text', width: 70 },
  Product: { type: 'text', width: 160 },
  Processor: { type: 'text', width: 170 },
  'CTC Date': { type: 'date', width: 100 },
  Notes: { type: 'text', width: 260 },
  'Days to CTC - App': { type: 'number', width: 90 },
  'Days to CTC - Sub': { type: 'number', width: 90 },
  'Client Review Request': { type: 'text', width: 130 },
  'Days To Closing': { type: 'number', width: 90 },
  loanID: { type: 'text', width: 220 },
  'Borrower Paid Transaction?': { type: 'text', width: 130 },
  'Final Interest Rate': { type: 'number', width: 100 },
  'Comp Formula Used': { type: 'text', width: 320 },
  Correspondent: { type: 'text', width: 130 },
  Investor: { type: 'text', width: 170 },
  'Min Number': { type: 'number', width: 100 },
  'Purchase Date': { type: 'date', width: 110 },
  'Deal Source': { type: 'text', width: 140 },
  'Payroll Batch': { type: 'text', width: 120 },
  'Payroll Flags': { type: 'text', width: 260 },
};

export function columnMeta(header) {
  const m = META[String(header || '').trim()] || { type: 'text', width: 140 };
  return { ...m, align: m.type === 'money' || m.type === 'number' ? 'right' : 'left' };
}

export const isEngineOwned = (header) => ENGINE_OWNED.has(String(header || '').trim());

// ---------------------------------------------------------------------------
// Prefs
// ---------------------------------------------------------------------------

const nonEmpty = (headers) => headers.filter((h) => String(h || '').trim() !== '');

/** Default prefs for the given raw sheet headers: the 18 defaults visible (in order), everything else hidden after them. */
export function defaultPrefs(sheetHeaders) {
  const raw = nonEmpty(sheetHeaders);
  const byTrim = new Map(raw.map((h) => [String(h).trim(), h]));
  const order = [];
  DEFAULT_ORDER.forEach((name) => {
    if (byTrim.has(name)) order.push(byTrim.get(name));
  });
  const rest = raw.filter((h) => !order.includes(h));
  return { order: [...order, ...rest], hidden: rest };
}

/** Load prefs from localStorage, reconciled against the live headers. */
export function loadPrefs(sheetHeaders, storage = window.localStorage) {
  const defaults = defaultPrefs(sheetHeaders);
  let stored = null;
  try {
    stored = JSON.parse(storage.getItem(STORAGE_KEY));
  } catch (e) {
    stored = null;
  }
  if (!stored || !Array.isArray(stored.order) || !Array.isArray(stored.hidden)) return defaults;

  const live = new Set(nonEmpty(sheetHeaders));
  const order = stored.order.filter((h) => live.has(h));
  const seen = new Set(order);
  const added = nonEmpty(sheetHeaders).filter((h) => !seen.has(h));
  return {
    order: [...order, ...added],
    hidden: [...stored.hidden.filter((h) => live.has(h)), ...added],
  };
}

export function savePrefs(prefs, storage = window.localStorage) {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({ order: prefs.order, hidden: prefs.hidden }));
  } catch (e) { /* storage full/blocked — prefs just won't persist */ }
}

export function toggleHidden(prefs, header) {
  const hidden = prefs.hidden.includes(header)
    ? prefs.hidden.filter((h) => h !== header)
    : [...prefs.hidden, header];
  return { ...prefs, hidden };
}

/** Move a header up (-1) or down (+1) in the order. */
export function move(prefs, header, delta) {
  const idx = prefs.order.indexOf(header);
  const to = idx + delta;
  if (idx === -1 || to < 0 || to >= prefs.order.length) return prefs;
  const order = [...prefs.order];
  order.splice(idx, 1);
  order.splice(to, 0, header);
  return { ...prefs, order };
}

export const visibleColumns = (prefs) => prefs.order.filter((h) => !prefs.hidden.includes(h));

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const SHEETS_EPOCH_MS = Date.UTC(1899, 11, 30);

/** Sheets date serial → "M/d/yy" (UTC parts — serials are timezone-less). */
export function serialToDateString(serial) {
  const d = new Date(SHEETS_EPOCH_MS + serial * 86400000);
  if (isNaN(d.getTime())) return String(serial);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${String(d.getUTCFullYear()).slice(-2)}`;
}

/** Display formatting for a cell (raw values in, strings out). */
export function formatCell(header, value) {
  if (value === '' || value === null || value === undefined) return '';
  const { type } = columnMeta(header);
  if (type === 'money' && typeof value === 'number') {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (type === 'date' && typeof value === 'number') return serialToDateString(value);
  return String(value);
}

/** The raw value → what the editor input should initially show. */
export function editableText(header, value) {
  if (value === '' || value === null || value === undefined) return '';
  const { type } = columnMeta(header);
  if (type === 'date' && typeof value === 'number') return serialToDateString(value);
  return String(value);
}
